import React, { useState, useEffect } from 'react';
import { getCarouselSlides, saveCarouselSlide, deleteCarouselSlide } from '../services/apiService';
import { supabase } from '../services/supabase';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import { Plus, Trash2, Edit2, Image as ImageIcon, Link as LinkIcon, X, Loader2, Eye, EyeOff } from 'lucide-react';

const AdminCarousel = () => {
  const [slides, setSlides] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const { toast, showToast, hideToast } = useToast();
  const [editingSlide, setEditingSlide] = useState(null);
  const [formData, setFormData] = useState({
    image_url: null,
    image_file: null,
    show_card: false,
    card_title: '',
    card_description: '',
    card_link: '',
    order: 0,
  });

  useEffect(() => {
    loadSlides();
  }, []);

  const loadSlides = async () => {
    try {
      setIsLoading(true);
      const data = await getCarouselSlides();
      const sorted = data.sort((a, b) => (a.display_order || a.order || 0) - (b.display_order || b.order || 0));
      setSlides(sorted);
    } catch (error) {
      console.error('Error loading slides:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        image_file: file,
      });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      let imageUrl = editingSlide?.image_url || editingSlide?.url;

      if (formData.image_file) {
        const fileExt = formData.image_file.name.split('.').pop();
        const fileName = `carousel_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `carousel/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('photos')
          .upload(filePath, formData.image_file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          console.error('Image upload error:', uploadError);
          let errorMessage = 'Failed to upload image.';
          if (uploadError.message?.includes('row-level security')) {
            errorMessage = 'Storage permission denied. Check RLS policies.';
          }
          showToast(errorMessage, 'error');
          setSaving(false);
          return;
        }

        const { data: { publicUrl } } = supabase.storage.from('photos').getPublicUrl(filePath);
        imageUrl = publicUrl;
      }

      await saveCarouselSlide({
        image_url: imageUrl,
        show_card: formData.show_card,
        card_title: formData.card_title || null,
        card_description: formData.card_description || null,
        card_link: formData.card_link || null,
        order: parseInt(formData.order) || 0,
        id: editingSlide?.id,
      });
      
      handleCloseForm();
      loadSlides();
      showToast(editingSlide ? 'Slide updated!' : 'Slide added!', 'success');
    } catch (error) {
      console.error('Error saving slide:', error);
      showToast('Error saving slide. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingSlide(null);
    setFormData({
      image_url: null,
      image_file: null,
      show_card: false,
      card_title: '',
      card_description: '',
      card_link: '',
      order: 0,
    });
    setPreviewUrl(null);
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormData({
      image_url: null,
      image_file: null,
      show_card: slide.show_card || false,
      card_title: slide.card_title || '',
      card_description: slide.card_description || '',
      card_link: slide.card_link || '',
      order: slide.display_order || slide.order || 0,
    });
    setPreviewUrl(slide.image_url || slide.url || null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this slide?')) {
      try {
        await deleteCarouselSlide(id);
        loadSlides();
        showToast('Slide deleted!', 'success');
      } catch (error) {
        console.error('Error deleting slide:', error);
        showToast('Error deleting slide. Please try again.', 'error');
      }
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Carousel Slides</h1>
            <p className="text-slate-500 text-xs md:text-sm mt-1">Manage home page banner images.</p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingSlide(null);
            setFormData({
              image_url: null,
              image_file: null,
              show_card: false,
              card_title: '',
              card_description: '',
              card_link: '',
              order: slides.length,
            });
            setPreviewUrl(null);
          }}
          className="w-full md:w-auto flex items-center justify-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20"
        >
          <Plus size={18} />
          Add Slide
        </button>
      </div>

      {/* Slides List */}
      {isLoading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-red-600 mb-4"></div>
          <p className="text-slate-500">Loading slides...</p>
        </div>
      ) : slides.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {slides.map((slide) => (
            <div key={slide.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden border border-slate-200 flex flex-col">
              
              {/* Image Container */}
              <div className="relative aspect-video bg-slate-100 group">
                {(() => {
                  const url = slide.image_url || slide.url;
                  const safeUrl = url && !url.startsWith('blob:') ? url : null;
                  return safeUrl ? (
                    <img
                      src={safeUrl}
                      alt="Carousel slide"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                  ) : null;
                })()}
                <div className="hidden w-full h-full items-center justify-center bg-slate-200 text-slate-400 text-sm">
                  Invalid Image
                </div>
                
                {/* Status Badges */}
                <div className="absolute top-2 right-2 flex gap-1">
                  <span className="bg-slate-900/70 text-white px-2 py-0.5 rounded text-[10px] font-bold backdrop-blur-sm">
                    #{slide.display_order || slide.order || 0}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold backdrop-blur-sm flex items-center gap-1 ${
                    slide.show_card ? 'bg-green-500/80 text-white' : 'bg-slate-200/80 text-slate-600'
                  }`}>
                    {slide.show_card ? <Eye size={10} /> : <EyeOff size={10} />}
                    {slide.show_card ? 'Card ON' : 'Card OFF'}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 flex-1 flex flex-col">
                {slide.show_card ? (
                   <div className="mb-3">
                      <div className="font-bold text-slate-800 text-sm mb-1 line-clamp-1">{slide.card_title || 'No Title'}</div>
                      <div className="text-xs text-slate-500 line-clamp-2">{slide.card_description || 'No description'}</div>
                      {slide.card_link && (
                        <div className="flex items-center gap-1 text-xs text-blue-600 mt-2 truncate">
                           <LinkIcon size={10} /> {slide.card_link}
                        </div>
                      )}
                   </div>
                ) : (
                    <div className="mb-3 text-xs text-slate-400 italic">
                        No overlay card configured. Image only.
                    </div>
                )}
                
                {/* Actions */}
                <div className="flex gap-2 mt-auto pt-3 border-t border-slate-50">
                  <button
                    onClick={() => handleEdit(slide)}
                    className="flex-1 bg-white border border-slate-200 text-slate-600 py-2 rounded-lg font-semibold hover:bg-slate-50 hover:text-blue-600 text-sm flex items-center justify-center gap-2 transition-colors"
                  >
                    <Edit2 size={14} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(slide.id)}
                    className="flex-1 bg-white border border-slate-200 text-slate-600 py-2 rounded-lg font-semibold hover:bg-red-50 hover:text-red-600 hover:border-red-100 text-sm flex items-center justify-center gap-2 transition-colors"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
            <ImageIcon size={32} />
          </div>
          <p className="text-lg font-bold text-slate-900">No slides yet</p>
          <p className="text-slate-500 text-sm mt-1">Add your first banner image.</p>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in slide-in-from-bottom md:zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 sticky top-0 z-10">
              <h2 className="text-lg font-bold text-slate-900">
                {editingSlide ? 'Edit Slide' : 'Add Slide'}
              </h2>
              <button onClick={handleCloseForm} className="text-slate-400 hover:text-red-600 transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Form Body */}
            <div className="overflow-y-auto p-6">
                <form id="carouselForm" onSubmit={handleSubmit} className="space-y-5">
                
                {/* Image Upload */}
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Slide Image *</label>
                    <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm file:mr-4 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-slate-200 file:text-slate-700 hover:file:bg-slate-300"
                    />
                    {previewUrl && (
                    <div className="mt-4 relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                        <img
                        src={previewUrl}
                        alt="Preview"
                        className="w-full h-40 object-cover"
                        />
                        <button
                        type="button"
                        onClick={() => {
                            setPreviewUrl(null);
                            setFormData({ ...formData, image_file: null });
                        }}
                        className="absolute top-2 right-2 bg-black/50 hover:bg-red-600 text-white p-1.5 rounded-full backdrop-blur-sm transition-colors"
                        >
                        <X size={14} />
                        </button>
                    </div>
                    )}
                </div>

                {/* Display Order */}
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Sort Order</label>
                    <input
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm"
                    min="0"
                    />
                </div>

                {/* Card Toggle */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                    <div className="relative">
                        <input
                        type="checkbox"
                        name="show_card"
                        checked={formData.show_card}
                        onChange={handleChange}
                        className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                    </div>
                    <span className="font-semibold text-sm text-slate-700">Show Overlay Card</span>
                    </label>

                    {/* Conditional Card Fields */}
                    {formData.show_card && (
                    <div className="mt-4 space-y-3 animate-in fade-in slide-in-from-top-2 pt-3 border-t border-slate-200">
                        <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Card Title</label>
                        <input
                            type="text"
                            name="card_title"
                            value={formData.card_title}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg focus:border-red-500 outline-none text-sm"
                            placeholder="e.g., Visa Approved!"
                        />
                        </div>
                        <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Description</label>
                        <textarea
                            name="card_description"
                            value={formData.card_description}
                            onChange={handleChange}
                            rows="2"
                            className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg focus:border-red-500 outline-none text-sm resize-none"
                            placeholder="e.g., Student Name → University"
                        />
                        </div>
                        <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Link (Optional)</label>
                        <input
                            type="text"
                            name="card_link"
                            value={formData.card_link}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg focus:border-red-500 outline-none text-sm"
                            placeholder="e.g., /visa-success-stories"
                        />
                        </div>
                    </div>
                    )}
                </div>
                </form>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex gap-3">
                <button
                    type="button"
                    onClick={handleCloseForm}
                    disabled={saving}
                    className="flex-1 py-2.5 rounded-lg font-medium text-slate-600 hover:bg-slate-100 transition-colors text-sm"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    form="carouselForm"
                    disabled={saving}
                    className="flex-1 bg-red-600 text-white py-2.5 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm flex items-center justify-center gap-2"
                >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : editingSlide ? 'Update' : 'Add'}
                </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} duration={toast.duration} onClose={hideToast} />}
    </div>
  );
};

export default AdminCarousel;